import React, { useContext, useEffect, useMemo } from 'react';
import { ITicketProps } from '../../../../types/Ticket/ITicket';
import UserInfo from './UserInfo';
import Ownership from './Ownership';
import TimeLogged from './TimeLogged';
import TicketType from './Type';
import { IUserProfile } from '../../../../types/User/IUserProfile';
import { RevUserContext, RevUserContextType } from '../../../../context/UserContext';
import Timeline from './Timeline';
import { Drawer, DrawerContent, TileLayout, TileLayoutItem, TilePosition } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import * as svg from '@progress/kendo-svg-icons';
import { UserApi } from '../../../../redux/api/UserApi';
import { Setting } from '../../../../types/Setting/Setting';
import { ISetting } from '../../../../types/User/ISetting';
import { PartialSpinny } from '../../../Nav/Spinny';
import Attachments from './Attachments';
import { FileApi } from '../../../../redux/api/FileApi';
import { FileType } from '../../../../types/File/FileType';
import { isMobile } from 'react-device-detect';
import { useReduxDispatch, useReduxSelector } from '../../../../redux/hooks';
import { setMenuExpanded } from '../../../../slices/TicketDetailUISlice';
import createTheme from '../../../../styles/themes';
import Stars from './Rating/Stars';
import Allocation from '../../../Gameplan/Allocation';
import Effort from '../../../Gameplan/Effort';

const Sidebar: React.FC<ITicketProps> = (props) => {
    const dispatch = useReduxDispatch();
    const { menuExpanded } = useReduxSelector(state => state.ticketDetailsUI);
    const [UpdateContactSetting, { isLoading: isSaving }] = UserApi.useSaveSettingMutation();
    const { data: userState } = UserApi.useGetSettingQuery(Setting.SIDEBAR_STATE);
    const { profile } = React.useContext<RevUserContextType>(RevUserContext)
    const { data: isFileActiveInternal } = FileApi.useIsActiveQuery(FileType.Call_Action_Internal, { skip: profile?.Features.External_Interface })
    const { data: isFileActiveSSI } = FileApi.useIsActiveQuery(FileType.Call_Action_External, { skip: !profile?.Features.External_Interface })
    const isActive = (profile?.Features.External_Interface && isFileActiveSSI) || (!profile?.Features.External_Interface && isFileActiveInternal);
    const typesActive = (profile?.Features.Call_Types && !profile?.Features.External_Interface) || (profile?.Features.SSI_Call_Type && profile?.Features.External_Interface);
    const handleReposition = e => {
        console.log('reposition', e);
        setData(e.value);
        console.log(e.value);
    };

    const defaultState = [{
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1,
    }, {
        col: 1,
        colSpan: 1,
        rowSpan: 1,
    }];
    const [data, setData] = React.useState<TilePosition[]>(defaultState);

    var defaultTileArray = useMemo(() => {
        if (userState?.String_Value) {
            var tileArray = JSON.parse(userState?.String_Value) as TilePosition[];
            //Add any missing tiles if the UI got updated
            if (tileArray.length < defaultState.length) {
                tileArray = [...tileArray, ...defaultState.slice(tileArray.length)];
            }
            return tileArray;
        }
    }, [userState]);

    useEffect(() => {
        if (defaultTileArray) {
            try {
                setData(defaultTileArray);
            } catch (error) {
                console.error('Error parsing sidebar state', error);
            }
        }
    }, [defaultTileArray]);

    const [editMode, setEditMode] = React.useState(false);

    const tiles = [
        props.Is_Workstream && {
            header: "Allocation",
            body: <Allocation {...props} editMode={editMode} />,
            resizable: false,
            className: 'tile'
        }, props.Is_Workstream && {
            header: "Effort",
            body: <Effort {...props} editMode={editMode} />,
            resizable: false,
            className: 'tile'
        }, {
            header: "User Info",
            body: <UserInfo {...props} />,
            resizable: false,
            className: 'tile'
        }, {
            header: undefined,
            body: <Stars  {...props} EditMode={editMode} />,
            resizable: false
        }, {
            header: undefined,
            body: <Timeline {...props} />,
            resizable: false
        }, !props.Is_Workstream && {
            header: "Ownership",
            body:
                <Ownership {...props} />,
            resizable: false
        }, isActive && {
            header: "Attachments",
            body:
                <Attachments {...props} editMode={editMode} />,
            resizable: false
        }, !props.Is_Workstream && {
            header: "Time logged",
            body: <TimeLogged {...props} />,
            resizable: false
        }, typesActive && {
            header: "Type",
            body:
                <TicketType {...props} />,
            resizable: false
        }].filter(Boolean) as TileLayoutItem[];


    const onSave = (state) => {
        const saveData = {
            Setting_Id: Setting.SIDEBAR_STATE,
            String_Value: state ? JSON.stringify(state) : null
        } as ISetting

        UpdateContactSetting(saveData).then(() => {
            ;

            setEditMode(false);
        });
    }

    const resetLayout = () => {
        if (!window.confirm('Are you sure you want to reset the sidebar layout?')) return;

        //console.log('resetting layout', defaultState, data);
        setData(() => defaultState);
        //console.log('resetting layout 1', defaultState, data);
        //saveChanges
        onSave(defaultState);
    }

    const onCancel = () => {
        console.log('onCancel', defaultTileArray, defaultState);
        setData(defaultTileArray ?? defaultState);
        setEditMode(false);
    }

    const handleMenuSelect = ev => {
        //console.log('handleMenuSelect', ev);
        dispatch(setMenuExpanded(false))
    };

    const userCanEdit = !(profile?.Features.External_Interface) && !isMobile
    const pageThemeCSS = createTheme(profile.Body_Hex_Color, profile?.Body_Style, profile?.Body_Image_Url);
    const renderSideBarItems = () => {
        return (
            <div style={pageThemeCSS} className={`sidebar-items col-md-3 position-relative justify-content-end pr-0 sidebar d-lg-flex ${editMode ? 'sidebar-edit' : ''}`}>
                {isMobile && <div className='d-flex justify-content-end mb-2 mr-2'><Button onClick={() => dispatch(setMenuExpanded(false))} svgIcon={svg.xIcon} className='ms-2' size='medium' fillMode='solid'></Button></div>}
                <div>
                    {isSaving && <PartialSpinny />}
                    {editMode ? <TileLayout rowHeight={'auto'} columns={1} positions={data} gap={{
                        rows: 10,
                        columns: 10
                    }} items={tiles} onReposition={handleReposition} />

                        :
                        tiles.sort((a, b) => {
                            return data[tiles.indexOf(a)]?.order - data[tiles.indexOf(b)]?.order
                        }).map((t, i) => <div key={i}>{t.body}</div>)
                    }
                    {userCanEdit && <div className='d-flex justify-content-center mt-2 mb-4'>
                        {!editMode ? <Button svgIcon={svg.gapRowIcon} className='edit-layout-button k-primary' size='small' fillMode='solid' onClick={() => setEditMode(!editMode)} title='Edit layout'>
                            {editMode ? 'Save' : ''}
                        </Button>
                            :

                            <><Button svgIcon={svg.saveIcon} className='edit-layout-button k-primary' size='small' fillMode='solid' onClick={() => onSave(data)} title='Save'>
                                Save
                            </Button>
                                <Button svgIcon={svg.cancelIcon} className='ms-2' size='small' fillMode='solid' onClick={onCancel} >
                                    Cancel
                                </Button><Button svgIcon={svg.undoIcon} className='ms-2' size='small' fillMode='solid' onClick={resetLayout} title='Reset layout' >
                                    Reset
                                </Button></>}
                    </div>}
                    {/* userState={JSON.stringify(userState?.String_Value)} */}
                    {/* data={JSON.stringify(data)} */}
                </div>
            </div>
        );
    }



    return (isMobile ?
        <Drawer expanded={menuExpanded || !isMobile} position={'start'} mode={isMobile ? 'overlay' : 'push'} mini={false} items={[{}]} item={renderSideBarItems} onOverlayClick={handleMenuSelect} >
            <DrawerContent>
                {props.children}
            </DrawerContent>
        </Drawer >
        : <>{renderSideBarItems()}{props.children}</>)

};

export default Sidebar;